import type { ContentPage } from '../types/content';

const rev = (n: number, savedAt: string, savedByName: string, summary: string, blocks: Record<string, string>) => ({
  id: `rev-${n}-${savedAt}`,
  revisionNumber: n,
  savedAt,
  savedByName,
  summary,
  blocks,
});

/**
 * Every public section's copy lives on one of these records so the administrator
 * content manager is the single editable source of truth. Blocks marked
 * isUnverifiedPlaceholder render with a visible pre-publication marker and are
 * listed in the Pre-launch Review queue.
 */
export const contentPages: ContentPage[] = [
  {
    id: 'page-home',
    key: 'home',
    title: 'Home',
    path: '/',
    publishState: 'published',
    updatedAt: '2026-08-24T09:12:00Z',
    updatedByName: 'A. Administrator',
    seo: {
      title: 'Velnoris Executive Network | Retained Executive Search',
      metaDescription:
        'Retained executive search, board and C-suite recruitment, and confidential leadership mandates for organizations in the United States and internationally.',
      canonicalPath: '/',
      noindex: false,
      socialImageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/70984bb4-60ff-42ee-8a7b-f319ef1949bf.jpg',
    },
    blocks: [
      { key: 'hero.eyebrow', label: 'Hero eyebrow', type: 'text', value: 'Retained Executive Search' },
      { key: 'hero.headline', label: 'Hero headline', type: 'heading', value: 'Leadership That Moves Organizations Forward' },
      {
        key: 'hero.supporting',
        label: 'Hero supporting text',
        type: 'richtext',
        value:
          'We connect organizations with senior leaders through discreet, research-led executive search. Every mandate is commissioned, every shortlist is evidenced, and no candidate is presented to a client without their recorded authorization.',
      },
      {
        key: 'intro.heading',
        label: 'Introduction heading',
        type: 'heading',
        value: 'A research practice first, a search firm second',
      },
      {
        key: 'intro.body',
        label: 'Introduction body',
        type: 'richtext',
        value:
          'Senior appointments fail more often on a poorly calibrated specification than on poor research. We spend disproportionate effort establishing what the role actually is, what the organization is genuinely willing to trade, and what would constitute success in the first eighteen months — then we map the addressable market and show you its boundary, not only its output.',
      },
      {
        key: 'intro.secondary',
        label: 'Introduction secondary body',
        type: 'richtext',
        value:
          'That discipline is what allows a board to have confidence that a shortlist is representative rather than convenient, and what allows a candidate to understand exactly why they were, or were not, appointed.',
      },
      { key: 'services.heading', label: 'Services heading', type: 'heading', value: 'How we are engaged' },
      {
        key: 'services.intro',
        label: 'Services introduction',
        type: 'richtext',
        value:
          'Twelve engagement types across executive, board and advisory work. Each is scoped in writing before it begins, with defined deliverables and documented confidentiality safeguards.',
      },
      { key: 'industries.heading', label: 'Industries heading', type: 'heading', value: 'Industries served' },
      {
        key: 'industries.intro',
        label: 'Industries introduction',
        type: 'richtext',
        value:
          'Practice depth is what allows research to be calibrated on scope rather than on job title. Each practice maintains its own market map and its own view of where leadership requirements are shifting.',
      },
      { key: 'process.heading', label: 'Process heading', type: 'heading', value: 'How a search is conducted' },
      {
        key: 'process.intro',
        label: 'Process introduction',
        type: 'richtext',
        value:
          'The same six stages apply to every retained mandate. Timelines are agreed per engagement; the sequence and the evidence requirements do not vary.',
      },
      { key: 'journeys.heading', label: 'Journeys heading', type: 'heading', value: 'Two paths through this platform' },
      {
        key: 'candidateJourney.intro',
        label: 'Candidate journey introduction',
        type: 'richtext',
        value:
          'Registration takes a few minutes and requires no documents. From there you control what is shared, with whom, and when.',
      },
      {
        key: 'employerJourney.intro',
        label: 'Employer journey introduction',
        type: 'richtext',
        value:
          'A search inquiry begins a scoping conversation, not a contract. Accounts are verified before any candidate information is released.',
      },
      { key: 'opportunities.heading', label: 'Featured opportunities heading', type: 'heading', value: 'Selected verified mandates' },
      {
        key: 'opportunities.intro',
        label: 'Featured opportunities introduction',
        type: 'richtext',
        value:
          'Commissioned mandates with a confirmed vacancy. Confidential mandates are shown with the client withheld and the verification status stated separately.',
      },
      { key: 'confidential.heading', label: 'Confidential search heading', type: 'heading', value: 'When a client cannot be named' },
      {
        key: 'confidential.body',
        label: 'Confidential search body',
        type: 'richtext',
        value:
          'Some searches must be conducted while an incumbent remains in seat. Where a client instructs us to withhold its identity, we record that instruction against the mandate and tell candidates plainly that a restriction applies and why. The client’s identity is released to a candidate before their profile is presented — never after.',
      },
      {
        key: 'confidential.rule',
        label: 'Confidential search rule',
        type: 'richtext',
        value:
          'Confidentiality is never used to manufacture urgency, and never to obscure whether a vacancy is confirmed. The verification label and the disclosure label are always shown separately, on every opportunity.',
      },
      { key: 'coverage.heading', label: 'Coverage heading', type: 'heading', value: 'Geographic coverage' },
      {
        key: 'coverage.intro',
        label: 'Coverage introduction',
        type: 'richtext',
        value: 'Search delivered in the United States and internationally.',
        isUnverifiedPlaceholder: true,
        placeholderNote:
          'Confirm which markets the firm covers directly and which are covered through documented arrangements. Do not describe an international network unless one exists.',
      },
      { key: 'research.heading', label: 'Research heading', type: 'heading', value: 'Research and market intelligence' },
      {
        key: 'research.body',
        label: 'Research body',
        type: 'richtext',
        value:
          'Our research function exists independently of any single mandate. It maintains market maps by practice, tracks leadership movement and organizational design, and produces the compensation observations we attach to opportunities — always with the sample size and collection date disclosed, because a range quoted without them is not data.',
      },
      { key: 'insights.heading', label: 'Insights heading', type: 'heading', value: 'Leadership insights' },
      { key: 'testimonials.heading', label: 'Testimonials heading', type: 'heading', value: 'In their words' },
      {
        key: 'testimonials.note',
        label: 'Testimonials note',
        type: 'richtext',
        value:
          'Client and candidate statements will appear here once written permission to publish, the date given, and the mandate referenced have been recorded for each.',
        isUnverifiedPlaceholder: true,
        placeholderNote: 'No testimonial may be published without recorded written permission and an evidenced source.',
      },
      { key: 'cta.heading', label: 'Final call-to-action heading', type: 'heading', value: 'Begin a conversation' },
      {
        key: 'cta.body',
        label: 'Final call-to-action body',
        type: 'richtext',
        value:
          'Whether you are considering your next appointment or your next leadership move, the first step is a conversation without obligation on either side.',
      },
    ],
    revisions: [
      rev(3, '2026-08-24T09:12:00Z', 'A. Administrator', 'Reworded confidential-search section for clarity.', {}),
      rev(2, '2026-08-11T14:40:00Z', 'A. Administrator', 'Removed placement statistics pending verification.', {}),
      rev(1, '2026-07-30T10:05:00Z', 'A. Administrator', 'Initial home page content.', {}),
    ],
  },
  {
    id: 'page-about',
    key: 'about',
    title: 'About Us',
    path: '/about',
    publishState: 'published',
    updatedAt: '2026-08-20T11:30:00Z',
    updatedByName: 'A. Administrator',
    seo: {
      title: 'About Us | Velnoris Executive Network',
      metaDescription:
        'Our search philosophy, confidentiality commitments, ethical recruitment standards and candidate privacy principles.',
      canonicalPath: '/about',
      noindex: false,
    },
    blocks: [
      { key: 'hero.headline', label: 'Page headline', type: 'heading', value: 'A firm built around discretion and evidence' },
      {
        key: 'overview.body',
        label: 'Company overview',
        type: 'richtext',
        value:
          'Velnoris Executive Network is a retained executive search firm working with boards, chief executives and investors on appointments where the outcome materially changes the organization. We work on an exclusive, commissioned basis, and we decline mandates we do not believe we can deliver well.',
      },
      {
        key: 'overview.established',
        label: 'Establishment and scale',
        type: 'richtext',
        value:
          'Founded [year]. [Number] consultants across [number] offices, supported by a dedicated research function.',
        isUnverifiedPlaceholder: true,
        placeholderNote:
          'Confirm founding year, consultant headcount and office count. Do not publish any figure that cannot be evidenced.',
      },
      {
        key: 'mission.body',
        label: 'Mission',
        type: 'richtext',
        value:
          'To make senior appointments that hold — by calibrating the requirement honestly, mapping the market completely, assessing against evidence rather than impression, and treating every candidate as a party to the process rather than its subject.',
      },
    ],
    revisions: [rev(1, '2026-08-20T11:30:00Z', 'A. Administrator', 'Initial about page content.', {})],
  },
  {
    id: 'page-for-candidates',
    key: 'for_candidates',
    title: 'For Candidates',
    path: '/for-candidates',
    publishState: 'published',
    updatedAt: '2026-08-18T08:00:00Z',
    updatedByName: 'A. Administrator',
    seo: {
      title: 'For Candidates | Velnoris Executive Network',
      metaDescription:
        'How registration, consent, presentation authorization and application tracking work for executives on this platform.',
      canonicalPath: '/for-candidates',
      noindex: false,
    },
    blocks: [
      { key: 'hero.headline', label: 'Page headline', type: 'heading', value: 'You control what is shared, and when' },
      {
        key: 'hero.body',
        label: 'Page introduction',
        type: 'richtext',
        value:
          'Registering places you in a research network, not on a job board. Nothing about you reaches an employer without a separate authorization you give for that specific mandate.',
      },
    ],
    revisions: [rev(1, '2026-08-18T08:00:00Z', 'A. Administrator', 'Initial content.', {})],
  },
  {
    id: 'page-for-employers',
    key: 'for_employers',
    title: 'For Employers',
    path: '/for-employers',
    publishState: 'published',
    updatedAt: '2026-08-18T08:10:00Z',
    updatedByName: 'A. Administrator',
    seo: {
      title: 'For Employers | Velnoris Executive Network',
      metaDescription:
        'How to commission a retained search, request a confidential mandate, and what the engagement process involves.',
      canonicalPath: '/for-employers',
      noindex: false,
    },
    blocks: [
      { key: 'hero.headline', label: 'Page headline', type: 'heading', value: 'Commission a search that can be explained' },
      {
        key: 'hero.body',
        label: 'Page introduction',
        type: 'richtext',
        value:
          'An inquiry begins a scoping conversation, not a contract. We will tell you plainly whether we believe we can deliver a credible shortlist before either of us commits.',
      },
    ],
    revisions: [rev(1, '2026-08-18T08:10:00Z', 'A. Administrator', 'Initial content.', {})],
  },
  {
    id: 'page-footer',
    key: 'footer',
    title: 'Footer',
    path: '/*',
    publishState: 'published',
    updatedAt: '2026-08-12T16:20:00Z',
    updatedByName: 'A. Administrator',
    seo: { title: 'Footer', metaDescription: '', canonicalPath: '/*', noindex: true },
    blocks: [
      {
        key: 'footer.positioning',
        label: 'Footer positioning line',
        type: 'richtext',
        value:
          'Retained Executive Search · Board and C-Suite Recruitment · Confidential Leadership Opportunities · Executive Career Services',
      },
      {
        key: 'footer.disclaimer',
        label: 'Footer recruitment disclaimer',
        type: 'richtext',
        value:
          'Velnoris Executive Network is engaged and paid by client organizations. We do not charge candidates for consideration and do not guarantee employment, interviews, presentation to a client, compensation or offers. Opportunity labels indicate verification status; a Proposed or Market-Matched Opportunity is not a confirmed vacancy.',
      },
      {
        key: 'footer.fraud',
        label: 'Footer anti-fraud notice',
        type: 'richtext',
        value:
          'We never request payment, banking details, identity documents by email, or account passwords. Any communication doing so is not from us — please report it.',
      },
      {
        key: 'footer.email',
        label: 'Business email',
        type: 'text',
        value: 'hello@velnorisexecutivenetwork.com',
      },
      {
        key: 'footer.hours',
        label: 'Business hours',
        type: 'text',
        value: 'Monday to Friday, [09:00–18:00 local time]',
        isUnverifiedPlaceholder: true,
        placeholderNote: 'Confirm operating hours and the time zone they refer to.',
      },
    ],
    revisions: [rev(1, '2026-08-12T16:20:00Z', 'A. Administrator', 'Initial footer content.', {})],
  },
];

export const getContentPage = (key: string): ContentPage | undefined => contentPages.find((p) => p.key === key);

/** Convenience accessor used across the public site. */
export const blockValue = (pageKey: string, blockKey: string): string =>
  getContentPage(pageKey)?.blocks.find((b) => b.key === blockKey)?.value ?? '';

export const blockRecord = (pageKey: string, blockKey: string) =>
  getContentPage(pageKey)?.blocks.find((b) => b.key === blockKey);
